import type { WhatsAppSignupStep } from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";
import type { Translate } from "../i18n/locale";

// Le parole di una registrazione su Meta non riuscita (piano M6, R2, Task 7b): il passo (`details.step` del `502
// whatsapp_signup_failed`) e il motivo, dal solo codice che il server registra (`failureCode` del WhatsApp della persona,
// `setupError` del collegamento, `details.failureCode`): il codice di Meta (`<code>` o `<code>:<subcode>`) o dell'adattatore e del
// server (`no_response`, `invalid_response`, `lock_lost`…). Chi legge è il titolare, non uno sviluppatore: ogni codice che si
// conosce ha la sua frase, uno che non si conosce vale la frase generica; il codice grezzo non si mostra mai, come per la telefonia.
// Gli stessi codici dicono anche perché Meta non ha accettato un modello (`metaError`, Task 8b), con le frasi dei modelli.

const STEP: Record<WhatsAppSignupStep, MessageKey> = {
  token: "whatsappSendingSettings.meta.steps.token",
  webhook: "whatsappSendingSettings.meta.steps.webhook",
  subscribed: "whatsappSendingSettings.meta.steps.subscribed",
  phone_number: "whatsappSendingSettings.meta.steps.phone_number",
};

/** Il passo della registrazione in parole; `null` per un passo che il browser non conosce. */
export function signupStepText(t: Translate, step: unknown): string | null {
  return typeof step === "string" && step in STEP ? t(STEP[step as WhatsAppSignupStep]) : null;
}

// I codici di Graph più comuni (le pagine degli errori di Meta): troppe richieste, guasto passeggero, permesso mancante.
const RATE_LIMITED = new Set(["4", "17", "32", "613", "80007", "130429", "131056", "http_429"]);
const META_DOWN = new Set(["1", "2", "131000"]);
const PERMISSION = new Set(["3", "10", "200", "294", "http_401", "http_403"]);
const INVALID = new Set(["invalid_response", "invalid_reference", "too_many_pages"]);

/** Il genere di un codice registrato: le frasi che lo dicono cambiano con quello che si stava facendo (registrare, mandare un modello). */
type FailureClass = "noResponse" | "lockLost" | "tokenRejected" | "rateLimited" | "metaDown" | "permission" | "invalidResponse" | "generic";

function failureClassOf(code: string | null): FailureClass {
  if (code === null || code === "") return "generic";
  if (code === "no_response") return "noResponse";
  if (code === "lock_lost") return "lockLost";
  const main = code.split(":")[0] ?? code;
  if (main === "190") return "tokenRejected";
  if (RATE_LIMITED.has(main)) return "rateLimited";
  if (META_DOWN.has(main) || /^http_5\d\d$/.test(main)) return "metaDown";
  if (PERMISSION.has(main)) return "permission";
  if (INVALID.has(main)) return "invalidResponse";
  return "generic";
}

/** La chiave del motivo di una registrazione su Meta non riuscita, dal codice registrato. */
export function failureReasonKey(code: string | null): MessageKey {
  return `whatsappSendingSettings.meta.reason.${failureClassOf(code)}`;
}

const TEMPLATE_FAILURE: Record<FailureClass, MessageKey> = {
  noResponse: "whatsappTemplatesSettings.meta.failure.noResponse",
  lockLost: "whatsappTemplatesSettings.meta.failure.lockLost",
  tokenRejected: "whatsappTemplatesSettings.meta.failure.connection",
  rateLimited: "whatsappTemplatesSettings.meta.failure.rateLimited",
  metaDown: "whatsappTemplatesSettings.meta.failure.metaDown",
  permission: "whatsappTemplatesSettings.meta.failure.permission",
  invalidResponse: "whatsappTemplatesSettings.meta.failure.generic",
  generic: "whatsappTemplatesSettings.meta.failure.generic",
};

/** La chiave del motivo di un modello che Meta non ha accettato (`metaError`, il codice di un nuovo invio `failed`; R2, Task 8b). */
export function templateFailureReasonKey(code: string | null): MessageKey {
  return TEMPLATE_FAILURE[failureClassOf(code)];
}

/** «Registrazione su Meta non riuscita …»: con il passo quando si sa, e sempre con il motivo. */
export function signupFailureText(t: Translate, step: unknown, code: string | null): string {
  const reason = t(failureReasonKey(code));
  const stepText = signupStepText(t, step);
  return stepText === null ? t("whatsappSendingSettings.meta.failed", { reason }) : t("whatsappSendingSettings.meta.failedAtStep", { step: stepText, reason });
}
